import { ArrayUtils, engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Machine } from "../GameComponents/Machine";
import { CreateFrameTimer, FrameTimerEvents } from "../Utils/FrameTimer";
import { PlayList } from "../Components/Sounds/PlayList";
import { AppContext } from "../../Common/AppContext";
import { ModuleManager } from "../Managers/ModuleManager";
import { DataManager } from "../Managers/DataManager";
import { GameData } from "../Channels/Common/Datas/GameData";
import { Symbols } from "../GameComponents/Symbols";
import { TeasingExtended } from "./TeasingExtended";
import { IReelTeasingAttributes } from "./TeasingComponentAbstract";
import { Symbol } from "../GameComponents/Symbol";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { Reel } from "../GameComponents/Reel";
import { ReelExtended } from "./ReelExtended";
import { Configuration } from "../Components/Configuration";
import { BaseView } from "../GameComponents/Base/BaseView";
import { GameManager } from "../Managers/GameManager";
import { FreeRoundBonus } from "../Channels/Common/Bonuses/FreeRoundBonus";

@engineComponent(ComponentTypes.DisplayObject)
export class MachineExtended extends Machine {
    protected _teasingExtended: TeasingExtended;
    private _forceStop: boolean = false;
    private alreadyTease: boolean;
    private _teasing: SwitchElement;
    protected _reels: Array<ReelExtended>;

    private rta: Array<IReelTeasingAttributes>;

    get forceStop(): boolean {
        return this._forceStop;
    }

    constructor() {
        super();
    }

    /**
     * Initialize reference correct symbols and create reels.
     * @method initialize
     * @param symbols Symbols
     * @param reels Array<Array<number>>
     * @return void
     */
    public initialize(
        symbols: Symbols,
        reels: Array<Array<number>>,
        teasingExtended?: TeasingExtended,
        teasing?: SwitchElement,
    ): void {
        this._teasingExtended = teasingExtended;
        this._teasing = teasing;
        super.initialize(symbols, reels);
    }

    /**
     * Start spinning reels.
     * @method spinStart
     * @param longRotation boolean
     * @param useTransform boolean
     * @param useTransform boolean
     * @param currentSymbols Array<Array<Symbol>>
     * @param id number
     * @return void
     */
    public spinStart(
        longRotation?: boolean,
        useTransform: boolean = false,
        currentSymbols?: Array<Array<Symbol>>,
        id: number = -1,
    ): void {
        // extended
        if (DEBUG) {
            this.log("spinStart");
        }

        this._teasingExtended.wasPlayedTeaserSound = false;

        this._stoppedFromServer = false;
        this._stoppedFromMachine = false;
        this._stoppedFromLegal = false;

        this._startSpinTime = Date.now();

        this.visible = true;
        this._reelsContainer.visible = true;

        this.actualReelStopped = -1;
        this._stoppedReelIndexes = 0;

        if (this._reels != null) {
            for (let i: number = 0; i < this._reelCount; i++) {
                if (this._reels[i] != null) {
                    // this._reelsContainer.children[i].visible = true;
                    if (this._stoppedSymbolsArray.length > 0) {
                        this._reels[i].visible = true;
                        this._reels[i].setData(this._stoppedSymbolsArray[i]);
                        this._reels[i].currentSymbols = [];
                        for (let j = 0; j < this._visibleSymbolCount; j++) {
                            this._reels[i].currentSymbols.push(currentSymbols[i][j]);
                            currentSymbols[i][j] = null;
                        }
                        this._reels[i].showSymbols(useTransform);
                        this._reels[i].spinStart();
                        if (i === 3) {
                            if (BaseView.TeasingContainer != null && !ModuleManager.freespinsActive()) {
                                BaseView.TeasingContainer.addChild(this._reels[i]);
                            }
                        }
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no stopped symbols");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no reel with index: " + i);
                    }
                }
            }

            if (longRotation != null && longRotation) {
                this._stopTimer.start(this._minSpinRotationLong);
            } else {
                this._stopTimer.start(this._minSpinRotation);
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no reels");
            }
        }

        PlayList.play("SND_BaseRotationStart");
        PlayList.loop("SND_BaseRotation", 0, true);
    }

    /**
     * Stop reels rotation.
     * @method spinStop
     * @param event any - from timer
     * @param force boolean - default false used when spin_stop was dispatch
     * @param fromServer boolean - default false used when spin_stop was dispatch from server
     * @param legal
     * @return void
     */
    public spinStop(event: any, force: boolean = false, fromServer: boolean = false, legal: boolean = false): void {
        this._forceStop = force;
        // extended
        if (fromServer) {
            this._stoppedFromServer = true;
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            if (this.verifyMinGameRoundDurationConditions()) {
                this._legalStopTimer = setTimeout(
                    () => this.spinStop(null, false, false, true),
                    this.getLegalRemainingSpinDuration(),
                );
            } else {
                this._stoppedFromLegal = true;
            }
        } else if (legal) {
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
        } else {
            this._stopTimer.stop();
            this._stoppedFromMachine = true;
        }

        if (force) {
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
            this._stopTimer.stop();
            this._stoppedFromMachine = true;
        }

        if (DEBUG) {
            this.log("spinStop: " + force);
        }

        if (this._stoppedFromServer && this._stoppedFromMachine && this._stoppedFromLegal) {
            if (
                !ModuleManager.freespinsActive() &&
                !(FreeRoundBonus.inFrb && FreeRoundBonus.winPayMethod === "cumulative")
            ) {
                DataManager.updateDataElement(GameData.DataTotalWin, "0");
                DataManager.updateDataElement(GameData.DataTotalWinDenom, "0");
            }

            if (force && this._teasing != null) {
                this._teasing.goToCase("EMPTY");
            }

            let rta: Array<IReelTeasingAttributes>;
            if (this._teasingExtended != null) {
                let reelTeasingAttributes = this._teasingExtended.checkOnInit({
                    force: force,
                    stoppedSymbols: this._stoppedSymbolsArray,
                });
                if (this._teasingExtended.teasingEnabled) {
                    rta = reelTeasingAttributes;
                    this.rta = reelTeasingAttributes;
                } else {
                    this.rta = [];
                }
            }

            if (this._reels != null) {
                for (let i: number = 0; i < this._reelCount; i++) {
                    if (this._reels[i] != null) {
                        if (this._stoppedSymbolsArray.length !== 0) {
                            this._reels[i].setData(this._stoppedSymbolsArray[i]);
                            if (typeof rta !== "undefined") {
                                this._reels[i].spinStop(force, rta[i].stopDelay);
                            } else {
                                this._reels[i].spinStop(force);
                            }
                        } else {
                            if (DEBUG) {
                                this.log("ERROR no stopped symbols");
                            }
                        }
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reel with index: " + i);
                        }
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no reels");
                }
            }
        }
    }

    /**
     * Dispatched onMachineStopped when all reels are stopped.
     * @method onReelStopped
     * @param reelIndex number - reel index
     * @return void
     */
    public onReelStopped(reelIndex: number): void {
        // extended
        if (DEBUG) {
            this.log("reel stopped: " + reelIndex);
        }

        PlayList.play("SND_BaseRotationEnd" + reelIndex);

        this._stoppedReelIndexes++;
        this.actualReelStopped = reelIndex;
        this._reels[reelIndex].visible = false;
        this.addChild(this._reels[reelIndex]);
        this.emit(Machine.ReelStopped);

        if (!this._forceStop && this.rta.length) {
            if (this._stoppedReelIndexes !== this._reelCount) {
                if (typeof this.rta[reelIndex + 1] !== "undefined" && this.rta[reelIndex + 1].action === 3) {
                    PlayList.play("SND_BaseFSTeaserTrans");
                }
            }

            if (this._stoppedReelIndexes == this._reelCount - 1) {
                if (this.alreadyTease) {
                    this._teasingExtended.wasPlayedTeaserSound = true;
                }
                this._teasingExtended.removeTeasing(false);
                this._reels[4].setIsTeasing(false);
            }
            this.tease(reelIndex);
        }

        if (this._stoppedReelIndexes === this._reelCount) {
            PlayList.stop("SND_BaseRotation", 10);
            if (this._forceStop && this.alreadyTease) {
                this._teasingExtended.removeTeasing(false);
            }

            this.alreadyTease = false;
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stopTimer.stop();
            if (BaseView.TeasingBackground != null) {
                if (BaseView.TeasingBackground.getCurrentCase() === "START" && !ModuleManager.freespinsEnabled()) {
                    BaseView.TeasingBackground.goToCase("END");
                }
                if (BaseView.TeasingBackground.getCurrentCase() === "EMPTY" && ModuleManager.freespinsEnabled()) {
                    BaseView.TeasingBackground.goToCase("START");
                }
            }
            PlayList.stop("SND_BaseTeaser");
            if (this._teasingExtended.wasPlayedTeaserSound && !ModuleManager.freespinsEnabled()) {
                PlayList.play("SND_BaseTeaserFail");
                PlayList.loop("SND_BaseLoop");
                PlayList.restoreVolume("SND_BaseLoop");
                this._teasingExtended.wasPlayedTeaserSound = false;
            }

            if (DEBUG) {
                const old = AppContext.gameConfiguration.MinGameRoundDuration;
                const current = AppContext.gameConfiguration.GameRoundMinSpan;
                const minDuration = current > 0 ? current : old > 0 ? old : 0;
                const spinDuration = Date.now() - this._startSpinTime;

                if (spinDuration > minDuration || !this.verifyMinGameRoundDurationConditions()) {
                    this.log("Spin time duration: " + spinDuration + " ms");
                } else {
                    console.log("%cError spin time duration:" + spinDuration + " ms", "color: #ff9900");
                }
            }

            this.emit(Machine.MachineStopped);
            if (this._teasing != null) {
                this._teasing.goToCase("EMPTY");
            }
            this._reelsContainer.visible = false;
        }
    }

    private tease(reelIndex: number): void {
        switch (reelIndex) {
            case 2:
                if (typeof this.rta[reelIndex + 1] !== "undefined" && this.rta[reelIndex + 1].action === 3) {
                    if (this._teasing != null && this._teasing.getCurrentCase() === "EMPTY") {
                        if (BaseView.TeasingBackground != null) {
                            BaseView.TeasingBackground.goToCase("START");
                        }
                        this._teasing.goToCase("TEASING_4-4");
                        PlayList.play("SND_BaseTeaser");
                        PlayList.setVolume("SND_BaseLoop", 0);
                        PlayList.stop("SND_FSLoop");
                        this.alreadyTease = true;
                        this._teasingExtended.wasPlayedTeaserSound = true;
                    }
                    this._reels[3].spinTease([10], 60);
                }
                break;
            case 3:
                break;
            case 4:
                break;
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/MachineExtended.ts
 **/